import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import {
    Form,
    FormSelector,
    Grid,
    IOption,
    PargodaRoundButton,
    Typography,
    useAppDispatch,
    useAppSelector,
    useTheme,
} from '../../../base';
import {
    jobApplicationActions,
    jobApplicationSelectors,
    jobsActions,
    jobsSelectors,
} from '../../../actions';
import PargodaModal from '../../PargodaModal';
import { useCallback, useEffect } from 'react';
import * as yup from 'yup';

interface ITransferCandidateForm {
    targetJob: IOption | null;
}

const transferCandidateFormSchema = (t: (key: string) => string) =>
    yup.object().shape({
        targetJob: yup
            .object()
            .shape({
                label: yup.string().required(),
                value: yup.string().required(),
            })
            .nullable()
            .required(t('transferCandidate.selectPosition')),
    });

const emptyTransferCandidateForm: ITransferCandidateForm = {
    targetJob: null,
};

const TransferCandidateModal = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const theme = useTheme();

    const methods = useForm<ITransferCandidateForm>({
        resolver: yupResolver(
            transferCandidateFormSchema(t)
        ) as Resolver<ITransferCandidateForm>,
        mode: 'onSubmit',
        defaultValues: emptyTransferCandidateForm,
    });

    const { control } = methods;

    const isTransferLoading = useAppSelector(
        jobApplicationSelectors.getIsTransferCandidateLoading
    );

    const isModalOpen = useAppSelector(
        jobApplicationSelectors.getIsTransferCandidateModalOpen
    );

    const transferModalData = useAppSelector(
        jobApplicationSelectors.getTransferCandidateModalData
    );

    const publishedJobsOptions = useAppSelector(
        jobsSelectors.getPublishedJobsOptions
    );

    const setIsModalOpen = useCallback(
        (isOpen: boolean) => {
            dispatch(
                jobApplicationActions.setIsTransferCandidateModalOpen(isOpen)
            );
        },
        [dispatch]
    );

    useEffect(() => {
        if (isModalOpen) {
            methods.reset(emptyTransferCandidateForm);
            dispatch(jobsActions.fetchPublishedJobsOptions());
        }
    }, [isModalOpen, methods, dispatch]);

    const transferSubmit = () => {
        const formData = methods.getValues();
        if (!transferModalData?.applicationId || !formData.targetJob?.value) {
            return;
        }
        dispatch(
            jobApplicationActions.transferCandidate({
                applicationId: transferModalData.applicationId,
                jobId: Number(formData.targetJob.value),
            })
        );
    };

    return (
        <PargodaModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            modalTitle={t('transferCandidate.modalTitle')}
            sx={{ width: '595px' }}
        >
            <Form methods={methods} onSubmit={transferSubmit}>
                <Grid container>
                    <Grid item xs={12}>
                        <Grid container>
                            <Grid item xs={12} pt={1} pb={2}>
                                <Typography
                                    variant="body1"
                                    color={theme.palette.grey[600]}
                                >
                                    {t('transferCandidate.description', {
                                        candidateName:
                                            transferModalData?.candidateName ??
                                            '',
                                    })}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} pb={3}>
                                <FormSelector
                                    name="targetJob"
                                    control={control}
                                    options={publishedJobsOptions}
                                    label={t(
                                        'transferCandidate.selectPosition'
                                    )}
                                />
                            </Grid>
                            <Grid
                                item
                                xs={6}
                                display="flex"
                                justifyContent="flex-end"
                                pb={2}
                            >
                                <PargodaRoundButton
                                    textColor={theme.palette.grey[600]}
                                    variant="outlined"
                                    loadingPosition="start"
                                    onClick={() => {
                                        setIsModalOpen(false);
                                    }}
                                    startIcon={<></>}
                                    loading={isTransferLoading}
                                    sx={{
                                        marginInlineEnd: theme.spacing(1.5),
                                        height: '48px',
                                        width: '214px',
                                        fontWeight: 600,
                                        fontSize: '16px',
                                    }}
                                >
                                    {t('transferCandidate.cancelButton')}
                                </PargodaRoundButton>
                            </Grid>
                            <Grid item xs={6} display="flex" pb={2}>
                                <PargodaRoundButton
                                    textColor={theme.palette.common.white}
                                    variant="contained"
                                    type="submit"
                                    loadingPosition="start"
                                    startIcon={<></>}
                                    loading={isTransferLoading}
                                    sx={{
                                        background: `linear-gradient(0deg,${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                        width: '214px',
                                        fontWeight: 600,
                                        fontSize: '16px',
                                    }}
                                >
                                    {t('transferCandidate.transferButton')}
                                </PargodaRoundButton>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Form>
        </PargodaModal>
    );
};

export default TransferCandidateModal;
